import React, { type ReactNode, useEffect } from 'react';
import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import AddonIcon from '@atlaskit/icon/core/app';
import Image from '@atlaskit/image';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { FullScreenModalDialog } from '@atlaskit/modal-dialog/full-screen';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { ForgeModalDeprecationMessage } from './ForgeModalDeprecationMessage';
import { type ModalExtension } from './util';
import { getADSModalWidth } from '../utils';
import { type Extension } from '../web-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { ConfluenceExtension } from '@atlassian/forge-ui-types';
import { useForgeUiAnalyticsEvent } from '../analytics/useForgeUiAnalyticsEvent';
import { getExtensionType } from '../utils/getExtensionType';

const styles = cssMap({
	modalWrapper: {
		paddingBottom: token('space.300'),
	},
});

const getExtensionIcon = (extension?: Extension): string | undefined => {
	return extension?.properties?.config?.icon ?? extension?.properties?.icon;
};

interface ForgeExtensionPointModalProps {
	children: ReactNode;
	extension?: Extension;
	modalExtension: ModalExtension;
	shouldShowFCFModalDeprecationMessage?: boolean;
}

const ModalWrapper = (props: {
	children: ReactNode;
	closeModalExtension: () => void;
	extensionType?: string;
	shouldCloseOnEscapePress?: boolean;
	shouldCloseOnOverlayClick?: boolean;
	width?: string;
}) => {
	const {
		closeModalExtension,
		extensionType,
		width,
		shouldCloseOnOverlayClick,
		shouldCloseOnEscapePress,
	} = props;

	const isFullscreenAllowed =
		(extensionType === ConfluenceExtension.MACRO &&
			fg('forge_enable_fullscreen_viewport_for_custom_config')) ||
		(extensionType === ConfluenceExtension.BYLINE &&
			fg('platform_add_modal_support_to_bylineitem_extension'));

	if (width === 'fullscreen' && isFullscreenAllowed) {
		return (
			<FullScreenModalDialog
				shouldCloseOnEscapePress={shouldCloseOnEscapePress}
				onClose={closeModalExtension}
			>
				{props.children}
			</FullScreenModalDialog>
		);
	}

	// Fallback to 'x-large' if width is 'fullscreen' but not supported
	const shouldFallbackFromFullscreen =
		width === 'fullscreen' &&
		!isFullscreenAllowed &&
		fg('forge-ui-add-fallback-for-fullscreen-modal-size');
	const modalWidth = shouldFallbackFromFullscreen ? 'x-large' : width;

	if (shouldFallbackFromFullscreen) {
		// eslint-disable-next-line no-console
		console.warn(
			'Forge fullscreen extension point modal is in EAP and only available for macro (non-production) and byline (all environments) modules. Please sign up here https://ecosystem.atlassian.net/servicedesk/customer/portal/1040/group/3496/create/18983. The modal will be rendered with xlarge width instead.',
		);
	}

	return (
		<Modal
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
			shouldCloseOnEscapePress={shouldCloseOnEscapePress}
			onClose={closeModalExtension}
			width={modalWidth}
		>
			{props.children}
		</Modal>
	);
};

export const ForgeExtensionPointModal = ({
	children,
	modalExtension,
	shouldShowFCFModalDeprecationMessage,
	extension,
}: ForgeExtensionPointModalProps): React.JSX.Element => {
	const {
		closeModalExtension,
		title,
		modalWidth,
		shouldCloseOnOverlayClick,
		shouldCloseOnEscapePress,
		viewOnClose,
	} = modalExtension;

	const environmentType = extension?.environmentType;
	const extensionType = extension ? getExtensionType(extension?.properties) : 'extension';
	const isMacroExtension =
		extensionType === 'extension' && extension?.type === ConfluenceExtension.MACRO;
	const extensionIcon = isMacroExtension ? getExtensionIcon(extension) : undefined;

	const ADSModalWidth = modalWidth ? getADSModalWidth(modalWidth) : undefined;

	const { trackExtensionLoaded, trackBridgeSucceed, trackBridgeFailed } =
		useForgeUiAnalyticsEvent();

	// Only tracking fullscreen modals
	useEffect(() => {
		if (modalWidth === 'fullscreen' && fg('forge_enable_fullscreen_viewport_for_custom_config')) {
			trackExtensionLoaded({
				forgeEnvironment: environmentType,
				renderType: 'UIKit',
				extensionType: extensionType,
				viewportSize: 'fullscreen',
			});
		}
	}, [modalWidth, extensionType, environmentType, trackExtensionLoaded]);

	const handleClose = async () => {
		if (viewOnClose && fg('forge-ui-on-close-bridge-method')) {
			try {
				await viewOnClose();
				trackBridgeSucceed({
					method: 'onClose',
					forgeEnvironment: extension?.environmentType,
				});
			} catch (error) {
				// Track failure but still close the modal
				trackBridgeFailed({
					method: 'onClose',
					errorName: error instanceof Error ? error.name : 'UnknownError',
					forgeEnvironment: extension?.environmentType,
				});
			} finally {
				closeModalExtension();
			}
		} else {
			closeModalExtension();
		}
	};

	return (
		<ModalWrapper
			shouldCloseOnEscapePress={shouldCloseOnEscapePress}
			closeModalExtension={handleClose}
			extensionType={extension?.type}
			width={ADSModalWidth}
			shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
		>
			<ModalHeader>
				{isMacroExtension &&
				modalWidth === 'fullscreen' &&
				fg('forge_enable_fullscreen_viewport_for_custom_config') ? (
					<Inline space="space.100" alignBlock="center">
						{extensionIcon ? (
							<Image
								src={extensionIcon}
								alt="Macro custom config icon"
								width="24px"
								height="24px"
							/>
						) : (
							<AddonIcon label="" color={token('color.icon.accent.gray')} />
						)}
						<ModalTitle>{title}</ModalTitle>
					</Inline>
				) : (
					<ModalTitle>{title}</ModalTitle>
				)}
				<IconButton
					appearance="subtle"
					icon={CrossIcon}
					label="Close Modal"
					onClick={handleClose}
				/>
			</ModalHeader>
			<ModalBody>
				{shouldShowFCFModalDeprecationMessage && extension ? (
					<Stack space="space.200">
						<ForgeModalDeprecationMessage extension={extension} />
						<Box xcss={styles.modalWrapper}>{children}</Box>
					</Stack>
				) : (
					<Box xcss={styles.modalWrapper}>{children}</Box>
				)}
			</ModalBody>
		</ModalWrapper>
	);
};
